import type { Maybe } from 'graphql/jsutils/Maybe';
import type { ReactElement } from 'react';

import { memo } from 'react';
import styled from 'styled-components';

import { NextChildren } from './NextChildren';
import { colors } from '../../shared/core/styles';
import type { NavigationItem } from '~/utilities/graphql/codegen';

const S = {
  ChildrenWrapper: styled.div<{ $isOpen: boolean; $height?: number }>`
    width: 100%;
    overflow: hidden;
    height: ${({ $isOpen, $height }) => ($isOpen && $height ? $height : 0)}px;
    transition: height 0.5s cubic-bezier(0.35, 0, 0.25, 1);
  `,

  Children: styled.ul`
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    margin: 0;
    padding: 0;
    list-style-type: none;
    border-top: 1px solid ${({ theme }) => theme.colors.border.subtle};
  `,

  Child: styled.li`
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};

    &:last-child {
      border-bottom: none;
    }
  `,

  ChildLabel: styled.button<{ $isOpen: boolean }>`
    width: 100%;
    font: inherit;
    cursor: pointer;
    color: ${({ $isOpen, theme }) =>
      $isOpen ? theme.colors.content.component.buttons.primary.rest : colors.ACCESSIBILITY_GREY};
    text-align: center;
    padding: 16px;
    position: relative;

    &:hover,
    &:focus-visible,
    &.active {
      color: ${({ theme }) => theme.colors.content.component.buttons.primary.rest};
    }

    &::before,
    &::after {
      position: absolute;
      top: 50%;
      content: ' ';
      right: 20px;
      margin-top: -1px;
      width: 10px;
      height: 2px;
      background-color: ${colors.ACCESSIBILITY_GREY};
    }

    &::before {
      transform: ${({ $isOpen }) => ($isOpen ? 'rotate(0)' : 'rotate(90deg)')};
      transition: transform 0.25s ease-in-out;

      @media (prefers-reduced-motion) {
        transition: none;
      }
    }
  `,
};

type ChildrenPanelProps = {
  childNode: Maybe<NavigationItem>[] | undefined;
  i: number;
  isOpen: boolean;
  openChildIndex: number | undefined;
  height: number;
  childrenHeights: { [k: number]: number };
  onParentRef: (el: HTMLDivElement) => void;
  onChildRef: (el: HTMLDivElement, j: number) => void;
  onChildLabelClick: (j: number) => void;
};

const ChildrenPanelComponent = ({
  childNode,
  i,
  isOpen,
  openChildIndex,
  height,
  childrenHeights,
  onParentRef,
  onChildRef,
  onChildLabelClick,
}: ChildrenPanelProps): ReactElement => (
  <S.ChildrenWrapper
    role="tabpanel"
    id={`inpage-tabpanel-${i}`}
    aria-labelledby={`inpage-tab-${i}`}
    aria-hidden={!isOpen}
    tabIndex={-1}
    $isOpen={isOpen}
    ref={(el: HTMLDivElement) => {
      if (el) {
        onParentRef(el);
      }
    }}
    $height={height}
  >
    <S.Children>
      {childNode?.map((node, j) => (
        <S.Child key={node?.uid ?? j}>
          <S.ChildLabel
            $isOpen={openChildIndex === j}
            id={`inpage-accordion-btn-${i}-${j}`}
            aria-expanded={openChildIndex === j}
            aria-controls={`inpage-accordion-panel-${i}-${j}`}
            tabIndex={!isOpen ? -1 : 0}
            onClick={() => onChildLabelClick(j)}
          >
            {node?.label}
          </S.ChildLabel>
          <NextChildren
            node={node}
            i={i}
            j={j}
            isOpen={openChildIndex === j}
            height={childrenHeights[j] ?? 0}
            isParentOpen={isOpen}
            onRef={el => onChildRef(el, j)}
          />
        </S.Child>
      ))}
    </S.Children>
  </S.ChildrenWrapper>
);

export const ChildrenPanel = memo(ChildrenPanelComponent);
